// src/pages/admin/ServicesPage.tsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiClient } from '@/lib/api';
import { Link } from 'react-router-dom';
import { fetchRailExpiring, registerTransit, type RailExpiring } from '@/lib/railApi';
import { adminPricingApi, PRODUCT_LABELS } from '@/lib/pricing';
import { ChevronDown, ChevronRight, Train } from 'lucide-react';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Badge } from '@/components/ui/Badge';
import { Notice } from '@/components/ui/Notice';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton, SkeletonLines } from '@/components/ui/Skeleton';
import { checkbox, fieldControlSm } from '@/components/ui/fieldStyles';

interface ApiService {
  id: string;
  name: string;
  upstream_url: string;
  credits_per_request: number;
  timeout_ms: number;
  health_check_path: string;
  is_active: boolean;
}

interface ServiceOperation {
  id: string;
  service_id: string;
  path_pattern: string;
  label: string;
  credits: number;
}

interface OperationLatencyRow {
  operation_path: string;
  requests: number;
  avg_ms: number | null;
  max_ms: number | null;
  min_ms: number | null;
}

const ServicesPage: React.FC = () => {
  const qc = useQueryClient();
  const { data: services = [], isLoading } = useQuery<ApiService[]>({
    queryKey: ['admin-services'],
    queryFn: () => apiClient.get('/admin/services').then(r => r.data),
  });
  // Services charged from the Pricing page (Mapbox-matched products) ignore the flat credits here.
  const { data: pricing } = useQuery({ queryKey: ['admin-pricing'], queryFn: adminPricingApi.get });
  const { data: railExpiring = [] } = useQuery<RailExpiring[]>({ queryKey: ['rail-expiring'], queryFn: fetchRailExpiring });
  const registerRailApi = useMutation({
    mutationFn: () => registerTransit(),
    onSuccess: () => { toast.success('Transit API registered'); qc.invalidateQueries({ queryKey: ['admin-services'] }); },
    onError: (e: any) => toast.error(e.response?.data?.error ?? 'Registration failed'),
  });
  const railRegistered = services.some(svc => svc.id === 'transit');

  const update = useMutation({
    mutationFn: ({ id, ...patch }: Partial<ApiService> & { id: string }) =>
      apiClient.patch(`/admin/services/${id}`, patch),
    onSuccess: () => { toast.success('Service updated'); qc.invalidateQueries({ queryKey: ['admin-services'] }); },
    onError: (e: any) => toast.error(e.response?.data?.error ?? 'Update failed'),
  });

  if (isLoading) {
    return (
      <div className="space-y-6" aria-hidden="true">
        <div><Skeleton className="h-8 w-56" /><Skeleton className="mt-2 h-4 w-80 max-w-full" /></div>
        {[0, 1, 2].map(i => <Skeleton key={i} className="h-36 rounded-2xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="API services"
        description="Where each product sends its requests, its timeout and health check, and its flat price when it isn't set on the Pricing page."
      />
      <div className="space-y-4">
        {services.map(svc => (
          <ServiceRow key={svc.id} service={svc} pricedAs={pricing?.services?.[svc.id]} onSave={patch => update.mutate({ id: svc.id, ...patch })} />
        ))}
        {services.length === 0 && (
          <Panel><EmptyState title="No services yet." description="Services registered with the gateway appear here." /></Panel>
        )}
      </div>
      <Panel title="Transit data">
        {!railRegistered && (
          <Notice
            tone="warning"
            className="mb-4"
            action={
              <Button size="sm" variant="outline" disabled={registerRailApi.isPending} onClick={() => registerRailApi.mutate()}>
                Register Transit API
              </Button>
            }
          >
            The transit API is not yet a gateway product, so the map and API customers cannot reach it.
          </Notice>
        )}
        {railExpiring.length === 0
          ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Train className="h-4 w-4 shrink-0" aria-hidden="true" />
              No timetable expires in the next 30 days, and no data is over 5 years old.
            </p>
          )
          : (
            <ul className="divide-y divide-border">
              {railExpiring.map(e => e.reason === 'old_survey' ? (
                <li key={e.operator_id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
                  <span className="text-foreground">{e.name} ({e.country}) · survey {e.survey_date}, over 5 years old</span>
                  <Badge tone="warning">Old survey</Badge>
                </li>
              ) : (
                <li key={e.operator_id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
                  <span className="text-foreground">{e.name} ({e.country}) · until <span className="font-mono tabular-nums">{e.valid_until}</span></span>
                  <Badge tone={e.days_left <= 0 ? 'error' : 'warning'}>
                    {e.days_left <= 0 ? `Expired ${-e.days_left} days ago` : `${e.days_left} days left`}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
      </Panel>
    </div>
  );
};

const ServiceRow: React.FC<{ service: ApiService; pricedAs?: string[]; onSave: (p: Partial<ApiService>) => void }> = ({ service, pricedAs, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(service);
  const [showPricing, setShowPricing] = useState(false);

  return (
    <section aria-label={service.name} className="rounded-2xl border border-border bg-surface p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <h2 className="truncate text-sm font-medium text-foreground">{service.name}</h2>
          <Badge tone={service.is_active ? 'success' : 'error'} dot>
            {service.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => { setForm(service); setEditing(false); }}>Cancel</Button>
              <Button size="sm" onClick={() => { onSave(form); setEditing(false); }}>Save</Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Edit</Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Upstream URL" value={form.upstream_url} editing={editing} mono onChange={v => setForm(f => ({ ...f, upstream_url: v }))} />
        {pricedAs?.length
          ? <div><p className="mb-1 text-xs text-muted-foreground">Price</p><Link to="/admin/pricing" className="text-accent-text hover:underline">Set on the Pricing page</Link></div>
          : <Field label="Credits per request (default)" value={String(form.credits_per_request)} editing={editing} type="number" mono onChange={v => setForm(f => ({ ...f, credits_per_request: +v }))} />}
        <Field label="Timeout (ms)" value={String(form.timeout_ms)} editing={editing} type="number" mono onChange={v => setForm(f => ({ ...f, timeout_ms: +v }))} />
        <Field label="Health path" value={form.health_check_path} editing={editing} mono onChange={v => setForm(f => ({ ...f, health_check_path: v }))} />
      </div>
      {editing && (
        <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-foreground">
          <input type="checkbox" className={checkbox} checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          Active
        </label>
      )}

      {pricedAs?.length ? (
        <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
          Charged as {[...new Set(pricedAs)].map(id => PRODUCT_LABELS[id] ?? id).join('; ')}, with monthly free allowances and volume bands.
          Change these on the <Link to="/admin/pricing" className="text-accent-text hover:underline">Pricing page</Link>.
        </p>
      ) : (
        <div className="mt-4 border-t border-border pt-3">
          <button
            type="button"
            aria-expanded={showPricing}
            className="inline-flex items-center gap-1 text-xs font-medium text-accent-text hover:underline"
            onClick={() => setShowPricing(s => !s)}
          >
            {showPricing ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
            Per-operation pricing
          </button>
          {showPricing && <ServiceOperationsPanel serviceId={service.id} defaultCredits={service.credits_per_request} />}
        </div>
      )}
    </section>
  );
};

const ServiceOperationsPanel: React.FC<{ serviceId: string; defaultCredits: number }> = ({ serviceId, defaultCredits }) => {
  const qc = useQueryClient();
  const [newOp, setNewOp] = useState({ path_pattern: '', label: '', credits: defaultCredits });

  const { data: operations = [], isLoading } = useQuery<ServiceOperation[]>({
    queryKey: ['service-operations', serviceId],
    queryFn: () => apiClient.get(`/admin/services/${serviceId}/operations`).then(r => r.data),
  });

  const { data: latency = [] } = useQuery<OperationLatencyRow[]>({
    queryKey: ['service-latency', serviceId],
    queryFn: () => apiClient.get(`/admin/services/${serviceId}/latency`).then(r => r.data),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['service-operations', serviceId] });

  const create = useMutation({
    mutationFn: () => apiClient.post(`/admin/services/${serviceId}/operations`, newOp),
    onSuccess: () => { toast.success('Override added'); setNewOp({ path_pattern: '', label: '', credits: defaultCredits }); invalidate(); },
    onError: (e: any) => toast.error(e.response?.data?.error ?? 'Failed to add override'),
  });

  const update = useMutation({
    mutationFn: ({ id, credits }: { id: string; credits: number }) =>
      apiClient.put(`/admin/services/operations/${id}`, { credits }),
    onSuccess: () => { toast.success('Override updated'); invalidate(); },
    onError: (e: any) => toast.error(e.response?.data?.error ?? 'Update failed'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/services/operations/${id}`),
    onSuccess: () => { toast.success('Override removed'); invalidate(); },
    onError: (e: any) => toast.error(e.response?.data?.error ?? 'Delete failed'),
  });

  const latencyFor = (pathPattern: string) => latency.find(l => l.operation_path === pathPattern);

  return (
    <div className="mt-3">
      <p className="mb-3 max-w-[80ch] text-xs text-muted-foreground">
        Charge different credits for specific sub-paths of this service (for example a heavier fuzzy address
        search against a cheap encode or decode) instead of one flat rate. Unmatched requests fall back to the
        default above. Average and maximum latency (last 7 days) show once real traffic exists, to help set the price.
      </p>

      {isLoading ? (
        <SkeletonLines count={2} className="mb-4" />
      ) : (
        <div className="mb-4 space-y-2">
          {operations.map(op => {
            const stats = latencyFor(op.path_pattern);
            return (
              <div key={op.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-background/40 px-3 py-2 text-sm">
                <span className="truncate font-mono text-xs text-foreground">{op.path_pattern}</span>
                <span className="min-w-[8rem] flex-1 truncate text-xs text-muted-foreground">{op.label}</span>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {stats ? `avg ${Math.round(stats.avg_ms ?? 0)}ms · ${stats.requests} req` : 'no data yet'}
                </span>
                <input
                  type="number"
                  aria-label={`Credits for ${op.path_pattern}`}
                  className={cn(fieldControlSm, 'w-24 font-mono tabular-nums')}
                  defaultValue={op.credits}
                  onBlur={e => {
                    const val = +e.target.value;
                    if (val !== op.credits) update.mutate({ id: op.id, credits: val });
                  }}
                />
                <Button size="sm" variant="ghost" className="text-error hover:text-error" onClick={() => remove.mutate(op.id)}>Remove</Button>
              </div>
            );
          })}
          {operations.length === 0 && (
            <p className="text-xs text-muted-foreground">No overrides yet. This service charges the flat default for every request.</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          aria-label="Path pattern"
          className={cn(fieldControlSm, 'w-full font-mono sm:w-44')}
          placeholder="path pattern e.g. reverse"
          value={newOp.path_pattern}
          onChange={e => setNewOp(f => ({ ...f, path_pattern: e.target.value }))}
        />
        <input
          aria-label="Label"
          className={cn(fieldControlSm, 'w-auto min-w-[10rem] flex-1')}
          placeholder="label e.g. Reverse geocode"
          value={newOp.label}
          onChange={e => setNewOp(f => ({ ...f, label: e.target.value }))}
        />
        <input
          type="number"
          aria-label="Credits"
          className={cn(fieldControlSm, 'w-24 font-mono tabular-nums')}
          value={newOp.credits}
          onChange={e => setNewOp(f => ({ ...f, credits: +e.target.value }))}
        />
        <Button size="sm" variant="outline" disabled={!newOp.path_pattern || !newOp.label} onClick={() => create.mutate()}>
          Add
        </Button>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; value: string; editing: boolean; type?: string; mono?: boolean; onChange: (v: string) => void }> = ({ label, value, editing, type = 'text', mono, onChange }) => (
  <div className="min-w-0">
    <p className="mb-1 text-xs text-muted-foreground">{label}</p>
    {editing
      ? <input type={type} aria-label={label} className={cn(fieldControlSm, mono && 'font-mono text-[13px]')} value={value} onChange={e => onChange(e.target.value)} />
      : <p className={cn('truncate text-foreground', mono && 'font-mono text-[13px] tabular-nums')} title={value}>{value}</p>}
  </div>
);

export default ServicesPage;
